import {
  STUDIO_ADD_OR_UPDATE_DESIGN,
  STUDIO_ADD_OR_UPDATE_DESIGN_SNAPSHOT,
  STUDIO_ADD_OR_UPDATE_PACKAGES,
  STUDIO_ADD_OR_UPDATE_PRODUCT,
  STUDIO_ADD_OR_UPDATE_REVISION,
  STUDIO_ADD_OR_UPDATE_RENDERS,
  STUDIO_ADD_OR_UPDATE_SPACE,
  STUDIO_BATCH_ADD_OR_UPDATE_PRODUCTS,
  STUDIO_DELETE_DESIGN,
  STUDIO_RECEIVE_PRODUCTS_PURCHASED,
  SELECT_HOTSPOT_PRODUCT,
  SELECT_PDP_HOTSPOT_PRODUCT,
  STUDIO_SET_ROUTER_PARAMS,
  STUDIO_SET_SHOW_HIDE_LOOKBOOK,
  STUDIO_SET_DESIGN_SELECTOR_ACTIVE,
  STUDIO_SET_PRODUCT_VIEW_ACTIVE,
  STUDIO_SET_LAYOUT_MODAL_ACTIVE,
  STUDIO_SET_LOCALLY_SELECTED_LAYOUT,

  STUDIO_TOGGLE_BANNER_Z_INDEX
} from './action-constants';

// =======
// REDUCER
// =======

// Redux best practice is to keep state minimal and normalized.
// So we should try and store objects with unique keys instead of nested.
// We should also keep the minimum state possible. If we can compute something
// from existing state then that logic should go into a selector and we can
// use reselect for memoization if the computation is expensive.
export const initialState = {
  spaces: {}, // mapping of space id to Space instance
  designs: {}, // mapping of design id to Design instance
  designsViewed: {}, // mapping of designs that have been viewed this session
  designSelectorActive: false, // Determines whether left hand design selector view is visible
  designSnapshots: {}, // mapping of design snapshot id to Design instance
  layoutModalActive: false, // whether or not to display the layout submission modal
  locallySelectedLayoutId: 0, // the id of the layout that has been selected
  packagesData: [], // an array of package data
  products: {}, // mapping of product id to Product instance
  productVariants: {}, // mapping of variantHash to linked product ids
  productViewActive: false, // true if the product view should be displayed
  purchasedProductIds: [], // products purchased by the logged in user
  selectedHotspotProduct: null,
  selectedPDPHotspotProductInfo: null,
  selectedHotspotUid: null, // this is required for swap
  routerParams: {}, // router params like spaceId, designId, and productId

  revisions: {}, // mapping of revision uuid to Revision instance
  renders: {}, // mapping of render uid to Render instance
  focusedRender: null, // Active focused renderId selected in design
  showLookbook: false, // whether or not to display the lookbook modal,
  bannerContainerZindex: 0 // current value of BannerContainer's z-index
};

export const appDataReducerKey = 'liveSwapAppData';
export const appDataReducer = (state = initialState, action) => {
  switch (action.type) {
    case STUDIO_ADD_OR_UPDATE_SPACE:
      const space = action.payload;
      return { ...state, spaces: { ...state.spaces, [space.id()]: space } };

    case STUDIO_ADD_OR_UPDATE_DESIGN:
      const design = action.payload;
      return { ...state, designs: { ...state.designs, [design.id()]: design } };

    case STUDIO_ADD_OR_UPDATE_DESIGN_SNAPSHOT:
      const designSnapshot = action.payload;
      return {
        ...state,
        designSnapshots: {
          ...state.designSnapshots,
          [designSnapshot.id()]: designSnapshot
        }
      };

    case STUDIO_ADD_OR_UPDATE_PRODUCT:
      const product = action.payload;
      return { ...state, products: { ...state.products, [product.id()]: product } };

    case STUDIO_DELETE_DESIGN: {
      const designToDelete = action.payload;
      const spaceId = designToDelete.parentSpaceId();
      const space = state.spaces[spaceId];

      let designs = {};
      Object.keys(state.designs)
        .filter(id => Number(id) !== designToDelete.id()) // get all the designs except the removed design
        .forEach(id => { designs[id] = state.designs[id]; }); // repopulate designs object

      // Create new space instance with deleted design removed
      const updatedSpace = space.makeCopyWithDesignRemoved(designToDelete.id());

      // update redux state with new space instance.
      return { ...state, designs, spaces: { ...state.spaces, [spaceId]: updatedSpace } };
    }

    case STUDIO_BATCH_ADD_OR_UPDATE_PRODUCTS: {
      let updatedProducts = { ...state.products }; // copy current state
      let updatedVariants = { ...state.productVariants };
      const newProducts = action.payload;
      newProducts.forEach(product => {
        updatedProducts[product.id()] = product;
        if (!updatedVariants[product.variantHash()]) {
          updatedVariants[product.variantHash()] = [];
        }
        // Remove duplicates
        const newVariantsSet = new Set([ ...updatedVariants[product.variantHash()], product.id() ]);
        // Spread into new array
        updatedVariants[product.variantHash()] = [ ...newVariantsSet ];
      });

      return {
        ...state,
        products: updatedProducts,
        productVariants: updatedVariants
      };
    }

    case STUDIO_ADD_OR_UPDATE_REVISION: {
      const revision = action.payload;
      return { ...state, revisions: { ...state.revisions, [revision.uuid()]: revision } };
    }

    case STUDIO_ADD_OR_UPDATE_RENDERS: {
      let renders = { ...state.renders }; // copy current state

      const newOrUpdatedRenders = action.payload;
      newOrUpdatedRenders.forEach(render => {
        renders[render.uid()] = render;
      });

      return {
        ...state,
        renders
      };
    }

    case STUDIO_RECEIVE_PRODUCTS_PURCHASED:
      // the products may not be loaded when we get the purchased IDs
      // so we'll just keep the purchased Ids in state
      return {
        ...state,
        purchasedProductIds: action.payload
      };

    case STUDIO_SET_ROUTER_PARAMS: {
      return { ...state, routerParams: action.payload };
    }

    case SELECT_HOTSPOT_PRODUCT:
      return {
        ...state,
        selectedHotspotProduct: action.payload.product,
        selectedHotspotUid: action.payload.hotspotUid,
        alternativesFromHotspot: action.payload.fromHotspot
      };

    case SELECT_PDP_HOTSPOT_PRODUCT:
      return {
        ...state,
        selectedPDPHotspotProductInfo: action.payload
      };

    case STUDIO_SET_SHOW_HIDE_LOOKBOOK:
      return { ...state, showLookbook: action.payload };

    case STUDIO_SET_DESIGN_SELECTOR_ACTIVE:
      return { ...state, designSelectorActive: action.payload };

    case STUDIO_SET_PRODUCT_VIEW_ACTIVE:
      return { ...state, productViewActive: action.payload };

    case STUDIO_ADD_OR_UPDATE_PACKAGES:
      return { ...state, packagesData: action.payload };

    case STUDIO_SET_LAYOUT_MODAL_ACTIVE:
      return { ...state, layoutModalActive: action.payload };

    case STUDIO_SET_LOCALLY_SELECTED_LAYOUT:
      return { ...state, locallySelectedLayoutId: action.payload };

    case STUDIO_TOGGLE_BANNER_Z_INDEX:
      return {
        ...state,
        bannerContainerZindex: action.payload
      };

    default:
      return state;
  }
};
